import { useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import amplifyLogo from "@/assets/amplify-logo-sm.webp";


const navLinks = [
  { href: "#properties", label: "Properties", isRoute: false },
  { href: "#about", label: "About", isRoute: false },
  { href: "#contact", label: "Contact", isRoute: false },
  { href: "/my-bookings", label: "My Bookings", isRoute: true },
  { href: "/wishlist", label: "Wishlist", isRoute: true, icon: true },
  { href: "/profile", label: "Profile", isRoute: true, authOnly: true },
  { href: "/admin/login", label: "Property Managers", isRoute: true },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { session, signOut } = useAuth();
  const location = useLocation();

  const handleAnchorClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const id = href.replace("#", "");
    if (location.pathname !== "/") {
      window.location.href = `/#${id}`;
      setMobileOpen(false);
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setMobileOpen(false);
  }, [location.pathname]);

  const visibleLinks = navLinks.filter((link) => !(link as any).authOnly || session);

  const renderLink = (link: typeof navLinks[0], onClick?: () => void) => {
    const isActive = link.isRoute && location.pathname === link.href;
    const className = `text-sm font-medium transition-colors flex items-center gap-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
      isActive
        ? "text-accent font-semibold underline underline-offset-8 decoration-2 decoration-accent hover:text-accent/80 hover:decoration-accent/80"
        : "text-muted-foreground hover:text-foreground"
    }`;
    if (link.isRoute) {
      return (
        <Link key={link.href} to={link.href} onClick={onClick} className={className} aria-current={isActive ? "page" : undefined}>
          {(link as any).icon && <Heart className="h-3.5 w-3.5" />}
          {link.label}
        </Link>
      );
    }
    return (
      <a key={link.href} href={link.href} onClick={(e) => { handleAnchorClick(e, link.href); onClick?.(); }} className={className}>
        {link.label}
      </a>
    );
  };

  const handleSkip = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target =
      (document.getElementById("main-content") as HTMLElement | null) ||
      (document.querySelector("main") as HTMLElement | null);
    if (target) {
      if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: false });
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <a
        href="#main-content"
        onClick={handleSkip}
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-md focus:bg-accent focus:text-accent-foreground focus:shadow-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background text-sm font-medium"
      >
        Skip to content
      </a>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-md border-b border-border py-3">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background hover:opacity-80 transition-opacity" aria-label="Amplify Vacations home">
            <img
              src={amplifyLogo}
              alt="Amplify Vacations"
              className="w-48 md:w-64 h-auto object-contain"
            />
          </Link>

          {/* Desktop nav links — spread to fill */}
          <div className="hidden md:flex flex-1 items-center justify-evenly px-8">
            {visibleLinks.map((link) => renderLink(link))}
          </div>

          {/* Desktop auth */}
          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/concierge"
              className="rounded-full bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground shadow-soft transition-colors hover:bg-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Find Direct Rates Worldwide
            </Link>
            {session ? (
              <button onClick={() => signOut()} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                Sign Out
              </button>
            ) : (
              <Link to="/guest/auth" className="text-sm font-medium text-accent hover:text-accent/80 transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 -mr-2 text-foreground hover:text-accent transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-card/95 backdrop-blur-md border-b border-border animate-fade-in">
          <div className="container mx-auto px-6 py-4 flex flex-col gap-4">
            {visibleLinks.map((link) => renderLink(link, () => setMobileOpen(false)))}
            <Link
              to="/concierge"
              onClick={() => setMobileOpen(false)}
              className="rounded-full bg-accent px-5 py-2.5 text-center text-sm font-semibold text-accent-foreground"
            >
              Find Direct Rates Worldwide
            </Link>
            {session ? (
              <button
                onClick={() => { signOut(); setMobileOpen(false); }}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-1 text-left rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                Sign Out
              </button>
            ) : (
              <Link
                to="/guest/auth"
                onClick={() => setMobileOpen(false)}
                className="text-sm font-medium text-accent hover:text-accent/80 transition-colors py-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default Navbar;
